import { Presenter, type IPresenterOwner } from 'wdc-cube'
import type { MainPresenter } from 'src/main/Main.presenter'
import FilterManager, { ObjectProperty, ValueProperty } from '../the-consumer-market/tcm_filter_manager'
import { summarizeFilterScope } from '../the-consumer-market/tcm_filter_summary'
import {
    MultipleOptionStringKeywordFilterScope,
    SelectFilterScope,
    StringKeywordFilterScope,
    type AppliedFilterSummary,
    type IFilterScope,
    type KeywordOption
} from '../the-consumer-market/tcm_scopes'
import type {
    EmpresaFilter,
    FilterSelectOption,
    MultipleStringKeywordFilter,
    SelectFilter,
    StringKeywordFilter
} from '../the-consumer-market/tcm_service'
import { TcmPresenterForFilterCatalog } from '../the-consumer-market/tcm_presenter_filter_pane/tcm_filter_catalog_presenter'
import { type FilterPaneOwner } from '../the-consumer-market/tcm_presenter_filter_pane/tcm_filter_types'
import { TcmPresenterForStringKeywordFilter } from '../the-consumer-market/tcm_presenter_filter_pane/tcm_filter_presenter_for_string_keyword'
import { TcmPresenterForStringMultiSelectFilter } from '../the-consumer-market/tcm_presenter_filter_pane/tcm_filter_presenter_for_string_multi_select'
import { TcmPresenterForSelectFilter } from '../the-consumer-market/tcm_presenter_filter_pane/tcm_filter_select_presenter'
import { filterCaptionMatches } from '../the-consumer-market/tcm_presenter_filter_pane/tcm_presenter_filter_pane'
import { AdFilterCatalogFormKeys, AdFilterCatalogShareKeys } from './ad_keys'
import { AdFilterPaneScope } from './ad_scopes'
import type { AdFilter, OpcoesResponse } from './ad_types'
import { formatCompetencia } from './ad_utils'

export type FilterPaneHost = IPresenterOwner & {
    readonly app: MainPresenter
    fetchData(source: ''): Promise<unknown>
    opcoes(): Promise<OpcoesResponse>
    onSavedFilterApplied(): Promise<void>
}

type FilterItem = {
    readonly scope: IFilterScope
    publish(): void
    clear(): void
    release(): void
    apply(values: unknown): void
}

const FILTER_MODULE = 'ad'

export const FILTROS_PADRAO = {
    operadoras: ['UNIMED BLUMENAU', 'UNIMED DE JOINVILLE', 'UNIMED GRANDE FLORIANÓPOLIS'],
    ufs: ['SC'],
    coberturas: ['MÉDICO-HOSPITALAR']
}

export class AdFilterPanePresenter<H extends FilterPaneHost>
    extends Presenter<AdFilterPaneScope, H>
    implements FilterPaneOwner
{
    constructor(owner: H, scope: AdFilterPaneScope) {
        super(owner, scope)
        this.filterManager.bindIsAdditiveModeOn(() => true)
        const app = owner.app
        this.catalog = new TcmPresenterForFilterCatalog(this, {
            app,
            module: FILTER_MODULE,
            buildData: () => this.buildSavedFilter(),
            openForm: (mode, sourceId) => new AdFilterCatalogFormKeys(app).setMode(mode).setSourceId(sourceId).flip(),
            openShare: (sourceId) => new AdFilterCatalogShareKeys(app).setSourceId(sourceId).flip()
        })
    }

    readonly catalog: TcmPresenterForFilterCatalog

    private readonly filterManager = new FilterManager()
    private readonly __items: FilterItem[] = []
    private readonly __keys = new Map<FilterItem, keyof AdFilter>()
    private __order = 0

    private readonly __operadorasProp = new ValueProperty<MultipleStringKeywordFilter>(this.filterManager)
    private readonly __registrosProp = new ValueProperty<StringKeywordFilter>(this.filterManager)
    private readonly __ufsProp = new ValueProperty<MultipleStringKeywordFilter>(this.filterManager)
    private readonly __coberturasProp = new ValueProperty<MultipleStringKeywordFilter>(this.filterManager)
    private readonly __dataCorteProp = new ObjectProperty<SelectFilter>(this.filterManager)

    private __operadoras?: TcmPresenterForStringMultiSelectFilter
    private __ufs?: TcmPresenterForStringMultiSelectFilter
    private __coberturas?: TcmPresenterForStringMultiSelectFilter
    private __dataCorte?: TcmPresenterForSelectFilter

    // :: Lifecycle

    async initialize() {
        this.scope.onToggleExpanded = this.action(this.__handleToggleExpanded)
        this.scope.onClearFilters = this.action(this.__handleClearFilters)
        this.scope.onApply = this.action(this.__handleApply)
        this.scope.onSearchFilterChanged = this.action(this.__handleSearchFilterChanged)

        const opcoes = () => this.owner.opcoes()
        const keywords = (lista: string[]): KeywordOption[] => lista.map((v) => ({ key: v, label: v }))

        this.__operadoras = await this.add(
            new TcmPresenterForStringMultiSelectFilter(
                this,
                new MultipleOptionStringKeywordFilterScope(),
                this.__operadorasProp,
                async () =>
                    (await opcoes()).operadoras.map((o) => ({
                        key: o.nome,
                        label: o.ativa ? o.nome : `${o.nome} (cancelada)`
                    }))
            ),
            'Nome da Operadora',
            'operadoras'
        )
        await this.add(
            new TcmPresenterForStringKeywordFilter(this, new StringKeywordFilterScope(), this.__registrosProp),
            'Registro ANS',
            'registros'
        )
        this.__ufs = await this.add(
            new TcmPresenterForStringMultiSelectFilter(
                this,
                new MultipleOptionStringKeywordFilterScope(),
                this.__ufsProp,
                async () => keywords((await opcoes()).ufs)
            ),
            'UF',
            'ufs'
        )
        this.__coberturas = await this.add(
            new TcmPresenterForStringMultiSelectFilter(
                this,
                new MultipleOptionStringKeywordFilterScope(),
                this.__coberturasProp,
                async () => keywords((await opcoes()).coberturas)
            ),
            'Cobertura Assistencial',
            'coberturas'
        )
        this.__dataCorte = new TcmPresenterForSelectFilter(this, new SelectFilterScope(), this.__dataCorteProp)
        await this.__dataCorte.prepareFilter(this.__order++, 'Data de Corte', await this.__opcoesDataCorte())
        this.__items.push(this.__dataCorte)
        this.__keys.set(this.__dataCorte, 'dataCorte')

        this.restoreDefaults()
        this.scope.filters = this.__items.map((item) => item.scope)

        await this.catalog.prepare()
        this.scope.catalogVisible = true
    }

    override release() {
        this.catalog.release()
        for (const item of this.__items) item.release()
        super.release()
    }

    // :: API

    restoreDefaults() {
        const marcar = (presenter: TcmPresenterForStringMultiSelectFilter | undefined, valores: string[]) => {
            if (presenter) presenter.getOrCreateIsIn().value = [...valores]
        }
        marcar(this.__operadoras, FILTROS_PADRAO.operadoras)
        marcar(this.__ufs, FILTROS_PADRAO.ufs)
        marcar(this.__coberturas, FILTROS_PADRAO.coberturas)
    }

    publish() {
        for (const item of this.__items) item.publish()
        // Os filtros desta página ficam nas propriedades do painel; o hasFilter() do FilterManager só
        // enxerga os campos do Mercado IA.
        this.scope.isFiltering = Object.values(this.buildFilter()).some((v) => v !== undefined)
        this.catalog.refreshDirtyState()
    }

    describeAppliedFilters(): AppliedFilterSummary[] {
        const result: AppliedFilterSummary[] = []
        for (const item of this.__items) {
            const summary = summarizeFilterScope(item.scope)
            summary && result.push(summary)
        }
        return result
    }

    buildFilter(): AdFilter {
        const list = <T>(prop: { size: number; values(): IterableIterator<T> }) =>
            prop.size > 0 ? [...prop.values()] : undefined
        return {
            operadoras: list(this.__operadorasProp),
            registros: list(this.__registrosProp),
            ufs: list(this.__ufsProp),
            coberturas: list(this.__coberturasProp),
            dataCorte: list(this.__dataCorteProp)
        }
    }

    buildSavedFilter(): EmpresaFilter {
        const data: Record<string, unknown> = { ...this.buildFilter() }
        for (const key of Object.keys(data)) {
            if (data[key] === undefined) delete data[key]
        }
        return data as EmpresaFilter
    }

    async apply(filter: EmpresaFilter) {
        const data = filter as Record<string, unknown>
        for (const item of this.__items) item.clear()
        this.filterManager.clear()
        for (const [item, key] of this.__keys) {
            if (data[key] !== undefined) item.apply(data[key])
        }
        await this.owner.onSavedFilterApplied()
    }

    // :: Internals

    private async add<P extends FilterItem & { prepare(order: number, label: string): Promise<void> }>(
        presenter: P,
        caption: string,
        key: keyof AdFilter
    ) {
        await presenter.prepare(this.__order++, caption)
        this.__items.push(presenter)
        this.__keys.set(presenter, key)
        return presenter
    }

    private async __opcoesDataCorte(): Promise<FilterSelectOption[]> {
        const { competencias } = await this.owner.opcoes()
        return competencias.map((c) => ({ id: c, title: formatCompetencia(c) }))
    }

    // :: Handlers

    private async __handleToggleExpanded() {
        this.scope.expanded = !this.scope.expanded
    }

    private async __handleClearFilters() {
        for (const item of this.__items) item.clear()
        this.filterManager.clear()
        await this.owner.fetchData('')
    }

    private async __handleApply() {
        await this.owner.fetchData('')
    }

    private async __handleSearchFilterChanged(text: string) {
        this.scope.searchFilterText = text
        for (const item of this.__items) {
            item.scope.visible = filterCaptionMatches(text, item.scope.caption)
        }
    }
}

export default AdFilterPanePresenter
